//#region types
import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels, WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { CurrencyEnums } from "models/enums";
import { TagsProps } from "models/propTypes/commonPropTypes";
//#endregion
import { DateHelper, FileHelper, LocalizationHelperInstance } from "utils/helpers";
import { store } from "store";
import cssExports from "theme/styles/main.module.scss";
import useAPIClientHelper from "./useAPIClientHelper";

function useSalaryDeviationsService() {
  const { apiClientInstance } = useAPIClientHelper();

  const get = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .SalaryDeviationClient.GetDeviation(id)
      .then((res) => res.data);
  };
  const create = (deviation: ApiModels.SalaryDeviationPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SalaryDeviationClient.CreateWithValidationSummary(deviation);
  };
  const update = (deviation: ApiModels.SalaryDeviationPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SalaryDeviationClient.Put(deviation);
  };
  const filter = (filter: ApiModels.SalaryDeviationFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).SalaryDeviationClient.GetDeviations(filter);
  };
  const reportFilter = (
    baseFilter: WintUtilsWebApi.Filter,
    formFilter: SalaryDeviationFormTypes.Filter,
    includePersonsWithNoDeviations?: boolean
  ) => {
    return filter({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
      includePersonsWithNoDeviations: includePersonsWithNoDeviations ?? false,
    });
  };
  const reportToAPIFilter = (filter: SalaryDeviationFormTypes.Filter) => {
    return {
      yearAndMonth: filter.yearAndMonth ? DateHelper.getYearMonth(filter.yearAndMonth) : 0,
      personId: filter.personId ?? 0,
      types: filter.types,
    };
  };
  const submitReport = (yearMonth: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SalaryDeviationClient.SubmitReport({ yearMonth: yearMonth });
  };
  const getSalaryDeviationDetailsText = (summary?: ApiModels.DeviationSummary) => {
    if (summary) {
      const formattedValue = LocalizationHelperInstance.getFormattedNumber(summary.value);
      switch (summary.unit) {
        case ApiModels.UnitOfDeviationValue.Km:
          return `${formattedValue} km`;
        case ApiModels.UnitOfDeviationValue.SEK:
          return LocalizationHelperInstance.getFormattedCurrency(summary.value, CurrencyEnums.CurrencyCode.SEK);
        case ApiModels.UnitOfDeviationValue.Days:
          return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfDays, "", {
            num: formattedValue,
          });
        case ApiModels.UnitOfDeviationValue.Hours:
          const hours = Math.trunc(summary.value);
          const mins = LocalizationHelperInstance.getFormattedNumber((summary.value - hours) * 60);

          if (mins && +mins > 0) {
            return `${hours} h  ${mins} min`;
          }

          return `${hours} h`;
        default:
          return formattedValue;
      }
    } else return "";
  };

  const deleteSalaryDeviation = (deviationId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SalaryDeviationClient.Delete(deviationId);
  };
  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useSalaryDeviationsPermissions)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const hasAdmin = salaryDeviationsService.hasAdminPermissions();
   *
   * @example
   * // New way (recommended):
   * function useSalaryDeviationsPermissions() {
   *   const loggedInUserIsSalaryAdmin = useSelector((state: AppState) => state.auth.selectedCompany.loggedInUserIsSalaryAdmin);
   *
   *   const hasAdminPermissions = () => {
   *     return loggedInUserIsSalaryAdmin;
   *   };
   *
   *   return { hasAdminPermissions };
   * }
   *
   * // In component:
   * const { hasAdminPermissions } = useSalaryDeviationsPermissions();
   * const hasAdmin = hasAdminPermissions();
   */
  const hasAdminPermissions = () => {
    return store.getState().auth.selectedCompany.loggedInUserIsSalaryAdmin;
  };
  const getMonth = (yearMonth: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company).SalaryDeviationClient.GetMonth(yearMonth);
  };
  const isMonthDisabled = (monthState?: ApiModels.SalaryDeviationMonthState) => {
    return monthState !== undefined && monthState !== ApiModels.SalaryDeviationMonthState.Created;
  };
  const getMonthToApprove = () => {
    const previousMonth = DateHelper.getFirstDayOfPreviousMonth(new Date());
    const currentMonth = DateHelper.getFirstDayOfMonth(new Date());
    const previousMonthAsNumber = DateHelper.getYearMonth(previousMonth);

    const deviationMonthToApprove = apiClientInstance(AuthEnums.AuthType.Company)
      .SalaryDeviationClient.GetMonth(previousMonthAsNumber)
      .then((res) => res.data)
      .then((res) => {
        if (!isMonthDisabled(res.state)) {
          return previousMonth;
        } else {
          return currentMonth;
        }
      })
      .catch(() => {
        return currentMonth;
      });

    return deviationMonthToApprove;
  };
  const getTodoState = (): TagsProps => {
    return {
      tags: [LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.ToApprove)],
      className: cssExports.yellowTag,
    };
  };

  const getSalarySpecifications = (baseFilter: WintUtilsWebApi.Filter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).SalarySpecificationDocumentClient.Filter(baseFilter);
  };

  const getSalarySpecificationsForUser = (baseFilter: WintSuperkollAPIModel.SalarySpecificationsFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).SalarySpecificationDocumentClient.FilterAll(baseFilter);
  };

  const getSalarySpecificationUsers = () => {
    return apiClientInstance(
      AuthEnums.AuthType.Company
    ).SalarySpecificationDocumentClient.GetSalarySpecificationsUsers();
  };

  const downloadSalarySpecification = (id: number) => {
    apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    })
      .SalarySpecificationDocumentClient.Download(id)
      .then((res) => {
        FileHelper.download(res);
      });
  };

  const generateSalarySpecification = () => {
    apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    })
      .SalarySpecificationDocumentClient.GenerateSalarySpec()
      .then((res) => {
        FileHelper.download(res);
      });
  };

  const isStateToApprove = (state: WintSuperkollAPIModel.SalaryDraftState) =>
    state === WintSuperkollAPIModel.SalaryDraftState.AtCustomer;

  const getColorOnSalaryDraftState = (state: WintSuperkollAPIModel.SalaryDraftState): string => {
    switch (state) {
      case WintSuperkollAPIModel.SalaryDraftState.AtCustomer:
        return cssExports.yellowTag;
      case WintSuperkollAPIModel.SalaryDraftState.Removed:
      case WintSuperkollAPIModel.SalaryDraftState.NotSent:
        return cssExports.grayTag;
      case WintSuperkollAPIModel.SalaryDraftState.Rejected:
        return cssExports.redTag;
      case WintSuperkollAPIModel.SalaryDraftState.Handled:
        return cssExports.greenTag;
      default:
        return cssExports.blueTag;
    }
  };

  const getById = async (draftId: number) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company).SalaryDraftsClient.GetSalaryDraft(draftId);
    return res.data;
  };

  // Due date for the salary deviation and time log report
  const getDueDateForTimeLog = (yearAndMonth: number) => {
    return DateHelper.addDays(DateHelper.getLastDayOfMonth(DateHelper.getDateFromYearMonth(yearAndMonth)), 5);
  };
  return {
    get,
    create,
    update,
    filter,
    reportFilter,
    reportToAPIFilter,
    submitReport,
    getSalaryDeviationDetailsText,
    deleteSalaryDeviation,
    hasAdminPermissions,
    getMonth,
    isMonthDisabled,
    getMonthToApprove,
    getTodoState,
    getSalarySpecifications,
    downloadSalarySpecification,
    generateSalarySpecification,
    isStateToApprove,
    getColorOnSalaryDraftState,
    getById,
    getDueDateForTimeLog,
    getSalarySpecificationsForUser,
    getSalarySpecificationUsers,
  };
}

export default useSalaryDeviationsService;
